import React, { useMemo } from 'react';
import {
  Box,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  TextField,
  Typography,
} from '@mui/material';
import { ProductFieldDefinition } from '../types/order';

function normalizeProductFieldOptions(raw: unknown): { value: string; label: string }[] {
  if (!Array.isArray(raw)) return [];
  const out: { value: string; label: string }[] = [];
  for (const it of raw) {
    if (it && typeof it === 'object' && !Array.isArray(it)) {
      const o = it as Record<string, unknown>;
      const value = String(o.value ?? '').trim();
      const label = String(o.label ?? o.value ?? '').trim() || value;
      if (value) out.push({ value, label });
    }
  }
  return out;
}

function fieldLabel(f: ProductFieldDefinition): string {
  return (f.label_override || '').trim() || (f.default_display_label || '').trim() || f.field_key;
}

export type StaffOrderFieldValues = Record<string, unknown>;

type StaffOrderProductFieldsFormProps = {
  fields: ProductFieldDefinition[];
  values: StaffOrderFieldValues;
  onChange: (next: StaffOrderFieldValues) => void;
};

/**
 * Dynamic capture form for order catalog product fields (Category / Subcategory product fields).
 */
function StaffOrderProductFieldsForm({ fields, values, onChange }: StaffOrderProductFieldsFormProps) {
  const visible = useMemo(
    () => [...fields].filter((f) => f.is_visible).sort((a, b) => a.display_order - b.display_order || a.field_key.localeCompare(b.field_key)),
    [fields],
  );

  if (visible.length === 0) {
    return (
      <Typography variant="body2" color="text.secondary">
        No per-product fields are configured for this catalog item. You can continue without extra details.
      </Typography>
    );
  }

  const setKey = (key: string, v: unknown) => {
    onChange({ ...values, [key]: v });
  };

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2, pt: 0.5 }}>
      {visible.map((f) => {
        const label = fieldLabel(f);
        const ft = (f.field_type || 'single_line_text').toLowerCase();
        const opts = normalizeProductFieldOptions(f.field_options);
        const val = values[f.field_key];

        if (ft === 'checkbox') {
          const checked = val === true || val === 'true' || val === '1' || String(val).toLowerCase() === 'yes';
          return (
            <FormControlLabel
              key={f.id}
              control={<Checkbox checked={checked} onChange={(e) => setKey(f.field_key, e.target.checked ? true : false)} />}
              label={label}
            />
          );
        }

        if (ft === 'checkboxes') {
          const arr = Array.isArray(val) ? (val as unknown[]).map((x) => String(x).trim()).filter(Boolean) : [];
          const toggle = (optVal: string, checked: boolean) => {
            const next = checked ? [...arr.filter((t) => t !== optVal), optVal] : arr.filter((t) => t !== optVal);
            setKey(f.field_key, next);
          };
          const choiceOpts = opts.length > 0 ? opts : [];
          if (choiceOpts.length === 0) {
            return (
              <TextField
                key={f.id}
                fullWidth
                label={label}
                value={typeof val === 'string' ? val : ''}
                helperText="This field has no options configured in Category Management."
                onChange={(e) => setKey(f.field_key, e.target.value)}
              />
            );
          }
          return (
            <Box key={f.id}>
              <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.5 }}>
                {label}
              </Typography>
              <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                {choiceOpts.map((o) => (
                  <FormControlLabel
                    key={o.value}
                    control={
                      <Checkbox
                        size="small"
                        checked={arr.includes(o.value)}
                        onChange={(e) => toggle(o.value, e.target.checked)}
                      />
                    }
                    label={o.label || o.value}
                  />
                ))}
              </Box>
            </Box>
          );
        }

        if (ft === 'dropdown') {
          if (opts.length === 0) {
            return (
              <TextField
                key={f.id}
                fullWidth
                label={label}
                value={typeof val === 'string' ? val : val != null ? String(val) : ''}
                helperText="Define dropdown options in Category Management."
                onChange={(e) => setKey(f.field_key, e.target.value)}
              />
            );
          }
          const sval = typeof val === 'string' ? val : val != null ? String(val) : '';
          return (
            <FormControl key={f.id} fullWidth size="small">
              <InputLabel>{label}</InputLabel>
              <Select label={label} value={sval} onChange={(e) => setKey(f.field_key, e.target.value as string)}>
                <MenuItem value="">
                  <em>Select…</em>
                </MenuItem>
                {opts.map((o) => (
                  <MenuItem key={o.value} value={o.value}>
                    {o.label || o.value}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          );
        }

        if (ft === 'multiline_text' || ft === 'rich_text') {
          const s = typeof val === 'string' ? val : val != null ? String(val) : '';
          return (
            <TextField
              key={f.id}
              fullWidth
              multiline
              minRows={ft === 'rich_text' ? 4 : 2}
              label={label}
              value={s}
              onChange={(e) => setKey(f.field_key, e.target.value)}
            />
          );
        }

        const s = typeof val === 'string' ? val : val != null ? String(val) : '';
        return <TextField key={f.id} fullWidth size="small" label={label} value={s} onChange={(e) => setKey(f.field_key, e.target.value)} />;
      })}
    </Box>
  );
}

export default StaffOrderProductFieldsForm;
